import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Ban, Lock, Search, ServerCrash } from "lucide-react";

interface ErrorPageProps {
  errorCode: 400 | 401 | 403 | 404 | 500;
}

const ErrorPage = ({ errorCode }: ErrorPageProps) => {
  const navigate = useNavigate();

  const getErrorDetails = () => {
    switch (errorCode) {
      case 400:
        return {
          title: "Bad Request",
          description: "Sorry, there was an error with your request. Please try again.",
          icon: <AlertTriangle className="h-16 w-16 sm:h-24 sm:w-24 text-yellow-500" />
        };
      case 401:
        return {
          title: "Unauthorized",
          description: "You need to be logged in to access this page.",
          icon: <Lock className="h-16 w-16 sm:h-24 sm:w-24 text-red-500" />
        };
      case 403:
        return {
          title: "Access Denied",
          description: "You don't have permission to access this page.",
          icon: <Ban className="h-16 w-16 sm:h-24 sm:w-24 text-red-500" />
        };
      case 404:
        return {
          title: "Page Not Found",
          description: "The page you're looking for doesn't exist or has been moved.",
          icon: <Search className="h-16 w-16 sm:h-24 sm:w-24 text-blue-500" />
        };
      case 500:
        return {
          title: "Server Error",
          description: "Something went wrong on our end. Please try again later.",
          icon: <ServerCrash className="h-16 w-16 sm:h-24 sm:w-24 text-red-500" />
        };
    }
  };

  const errorDetails = getErrorDetails();

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow flex items-center justify-center p-4 sm:p-6 lg:p-8 mt-16">
        <div className="text-center space-y-4 sm:space-y-6 max-w-lg mx-auto px-4">
          <div className="flex justify-center">
            {errorDetails?.icon}
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">
            {errorDetails?.title}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground">
            {errorDetails?.description}
          </p>
          <div className="pt-4 sm:pt-6">
            <Button
              onClick={() => navigate("/")}
              size="lg"
              className="min-w-[200px] text-base sm:text-lg py-6"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ErrorPage;